import {
  HoverTooltip,
  Loading,
  LoanStatus,
  PrimaryButton,
  TTableCellContent
} from '../atoms';
import {
  DisplayTable,
  DisplayTableHead,
  DisplayTableRow,
  DisplayTablePaginationRow
} from '../molecules';
import { useMambuLoanStore } from '../../contexts';
import type { TButtonType, TLoan } from '../../common';
import type { TLoans } from '../types';
import React from 'react';

type TLoanList = {
  loans: TLoans;
  productType: string;
  isLoading?: boolean;
};

type TTableSettings = {
  colWidths: Array<string>;
  colWidthsMd: Array<string>;
  cols: Array<string>;
  buttonText: string;
  buttonModalType?: TButtonType;
  modalTitle: string;
};

const getTableSettings = (productType: string): TTableSettings => {
  const settings: {
    [key: string]: TTableSettings;
  } = {
    PAS: {
      colWidths: ['140px', '154px', '154px', '140px', '110px', '210px', 'auto'],
      colWidthsMd: [
        '7rem',
        '11rem',
        '11rem',
        '9rem',
        '7.5rem',
        '7.5rem',
        'auto'
      ],
      cols: [
        '',
        'Borrower',
        'Student',
        'Plan Amount',
        'Frequency',
        'Status',
        'Course Name',
        'Plan Status'
      ],
      buttonText: 'Activate Plan',
      buttonModalType: 'activatePlan',
      modalTitle: 'Payment Assist'
    },
    ZAS: {
      colWidths: ['140px', '154px', '154px', '140px', '110px', '210px', 'auto'],
      colWidthsMd: [
        '7rem',
        '11rem',
        '11rem',
        '9rem',
        '7.5rem',
        '7.5rem',
        'auto'
      ],
      cols: [
        '',
        'Borrower',
        'Student',
        'Plan Amount',
        'Frequency',
        'Status',
        'Course Name',
        'Plan Status'
      ],
      buttonText: 'Activate Plan',
      buttonModalType: 'activatePlan',
      modalTitle: 'Payment Assist'
    },
    PAD: {
      colWidths: ['140px', '154px', '154px', '140px', '110px', '210px', 'auto'],
      colWidthsMd: [
        '7rem',
        '11rem',
        '11rem',
        '9rem',
        '7.5rem',
        '7.5rem',
        'auto'
      ],
      cols: [
        '',
        'Borrower',
        'Student',
        'Plan Amount',
        'Frequency',
        'Status',
        'Course Name',
        'Plan Status'
      ],
      buttonText: 'Enter Course End Date',
      buttonModalType: 'courseEndDate',
      modalTitle: 'Deferred Payment Assist'
    },
    SNPL: {
      colWidths: ['150px', '164px', '164px', '160px', 'auto'],
      colWidthsMd: ['7rem', '9rem', '9rem', '9rem', 'auto'],
      cols: [
        '',
        'Borrower',
        'Student',
        'Plan Amount',
        'Course Name',
        'Funding Status'
      ],
      buttonText: 'Request Funds',
      buttonModalType: 'requestFund',
      modalTitle: productType
    }
  };
  settings.default = settings.SNPL;
  return settings[productType] || settings.default;
};

const loanCells = ({
  originalAccountNumber,
  creationDate,
  borrower,
  student,
  planAmount,
  repaymentFrequency,
  mambuAccountState,
  fundingStatus,
  courseEndDate,
  productType,
  pol,
  polSubCategory
}: TLoan) => {
  let isDataIncomplete = false;
  if (
    borrower === 'NO_DATA' ||
    student === 'NO_DATA' ||
    planAmount === 'NO_DATA' ||
    pol === 'NO_DATA'
  ) {
    isDataIncomplete = true;
  }
  const courseName =
    polSubCategory && polSubCategory !== 'NO_DATA' ? polSubCategory : pol;
  const cells: Array<TTableCellContent> = [
    {
      contentType: 'idAndDate',
      content: [originalAccountNumber, creationDate, isDataIncomplete]
    },
    {
      contentType: 'text',
      content: borrower
    },
    {
      contentType: 'text',
      content: student
    },
    {
      contentType: 'currency',
      content: planAmount
    }
  ];
  if (productType === 'PAS' || productType === 'ZAS' || productType === 'PAD') {
    cells.push({
      contentType: 'text',
      content:
        repaymentFrequency !== 'UNKNOWN_FREQUENCY' ? repaymentFrequency : ''
    });
    cells.push({
      contentType: 'jsx',
      content: <LoanStatus statusText={mambuAccountState} small />
    });
  }
  cells.push({
    contentType: 'text',
    content: courseName
  });
  return cells;
};

const LoanList = ({ productType, loans, isLoading }: TLoanList) => {
  const pagination = useMambuLoanStore((s) => s.searchPagination);
  const setSearchPagination = useMambuLoanStore((s) => s.setSearchPagination);

  const setPage = (newPage: number) =>
    setSearchPagination({
      ...pagination,
      currentPage: newPage
    });

  if (!loans?.length) {
    if (isLoading) {
      return <Loading />;
    } else {
      return (
        <DisplayTable colWidths={['auto']} colWidthsMd={['auto']}>
          <DisplayTableRow
            type="div"
            buttonModalType={undefined}
            cells={[
              {
                contentType: 'error',
                content: 'No loans found'
              }
            ]}
          />
        </DisplayTable>
      );
    }
  }

  const { colWidths, colWidthsMd, cols } = getTableSettings(productType);

  return (
    <DisplayTable
      colWidths={colWidths}
      colWidthsMd={colWidthsMd}
      hasButton={true}
    >
      <DisplayTableHead cols={cols} />
      {isLoading ? (
        <Loading />
      ) : (
        loans.map((loan: TLoan) => {
          const {
            originalAccountNumber,
            borrower,
            buttonEligibility,
            idCheckRequired,
            productType: prodType
          } = loan;
          const { buttonText, buttonModalType, modalTitle } =
            getTableSettings(prodType);
          return (
            <DisplayTableRow
              key={originalAccountNumber + '' + borrower}
              type="modal"
              cells={loanCells(loan)}
              modalType="loan"
              modalTitle={modalTitle}
              modalLoanContent={loan}
              button={
                <HoverTooltip text="Pending ID" show={idCheckRequired}>
                  <PrimaryButton disabled={idCheckRequired}>
                    {buttonText}
                  </PrimaryButton>
                </HoverTooltip>
              }
              buttonModalType={buttonModalType}
              showModal={buttonEligibility}
            />
          );
        })
      )}
      {pagination && (
        <DisplayTablePaginationRow pagination={pagination} setPage={setPage} />
      )}
    </DisplayTable>
  );
};

export { LoanList };
